/**
 * Small HTTP helpers for the few call sites that use fetch() directly, plus one
 * place to turn any request failure into a human message.
 *
 * fetch() resolves on a 4xx/5xx, so `fetch(...).then(r => r.json())` treats a
 * server error as success. fetchJson checks r.ok and throws an HttpError whose
 * message is the server's own (`error`, `message` or `detail` in a JSON body)
 * when it sent one, else a sentence for the status — the same wording
 * lib/apiError gives axios errors.
 */
import { apiErrorText } from '@/lib/apiError'

export class HttpError extends Error {
  readonly status: number
  readonly body: unknown
  constructor(message: string, status: number, body: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.body = body
  }
}

export async function fetchJson<T = any>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const r = await fetch(input, init)
  const text = await r.text()
  let body: unknown = undefined
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }
  if (!r.ok) {
    const msg = apiErrorText({ response: { status: r.status, data: body } }) ?? `Request failed (${r.status})`
    throw new HttpError(msg, r.status, body)
  }
  return body as T
}

/**
 * "<what failed>: <why>" for a toast, from an axios error (via lib/apiError) or
 * a fetchJson HttpError. Just `fallback` when there is nothing more specific.
 */
export function errorMessage(err: unknown, fallback: string): string {
  const why = err instanceof HttpError ? err.message : apiErrorText(err) ?? plainMessage(err)
  return why ? `${fallback.replace(/\.$/, '')}: ${why}` : fallback
}

function plainMessage(err: unknown): string | undefined {
  const m = (err as any)?.message
  return typeof m === 'string' && m.trim() && !/^Request failed with status code/.test(m) ? m : undefined
}
